/* eslint-disable no-restricted-syntax */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  withStyles,
} from '@material-ui/core';
import PF2MDataTable from '~/components/PF2MDataTable';
import styles from '../styles';
import { useTranslation } from 'react-i18next';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteChatCategoryItem,
  createChatCategoryItem,
  setChatCategoryValues,
  updateChatCategory,
  resetChatCategory,
  createChatResponseItem,
  resetChatResponse,
  updateChatResponse,
  setChatResponseValues,
  deleteChatResponseItem,
  getChatCategory,
  getChatResponse,
} from '~/store/dispatch/actions';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';

const MessagesRegistationTab = ({ classes }) => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch();
  const [loadedData, setLoadedData] = useState(false);
  const chatCategory = useSelector(state => state.dispatch.chatCategory);
  const chatResponse = useSelector(state => state.dispatch.chatResponse);
  const equipmentTypes = useSelector(state => state.nref.equipmentTypes);
  const equipmentGroups = useSelector(state => state.nref.equipmentGroups);
  const [alertOpenCategory, setAlertOpenCategory] = useState(false);
  const [alertOpenResponse, setAlertOpenResponse] = useState(false);

  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!loadedData) {
      dispatch(getChatCategory());
      dispatch(getChatResponse());
      setLoadedData(true);
    }
  }, [loadedData, dispatch]);

  useEffect(() => {
    if (chatCategory.length > 10) {
      setAlertOpenCategory(true);
    }
  }, [chatCategory.length]);

  const chatCategoryOptions = chatCategory.map(category => ({
    id: category.id,
    name: category.name,
  }));

  const renderAllOption = (value, options) => (
    value === 0 || value === null
      ? translate('common:All')
      : options.find(opt => opt.id === value)?.name
  );

  const equipmentTypeOptions = [
    { id: 0, name: translate('common:All') },
    ...equipmentTypes,
  ];

  const equipmentGroupOptions = [
    { id: 0, name: translate('common:All') },
    ...equipmentGroups,
  ];

  const formattedChatResponses = chatResponse.map(item => ({
    id: item.id,
    chat_message_category_id: item.chat_message_category_id,
    name: item.name,
    equipment_type_id:
      item.equipment_type_id === null ? 0 : item.equipment_type_id,
    equip_group_id: item.equip_group_id === null ? 0 : item.equip_group_id,
    active: item.active,
    isNew: item.isNew ?? false,
  }));

  const formattedCategories = chatCategory.map(item => ({
    id: item.id,
    name: item.name,
    equipment_type_id:
      item.equipment_type_id === null ? 0 : item.equipment_type_id,
    equip_group_id: item.equip_group_id === null ? 0 : item.equip_group_id,
    active: item.active,
  }));

  const validateMaxLengthField = key => (rowData) => {
    const value = rowData[key];
    if (!value || value.trim() === '') {
      return { isValid: false, helperText: translate('validation:RequiredField') };
    }
    if (value.length > 55) {
      return {
        isValid: false,
        helperText: translate('validation:maxLength55'),
      };
    }
    return {
      isValid: true,
      helperText: '',
    };
  };

  const validateLookupField = key => (rowData) => {
    if (['', '0'].includes(rowData[key])) {
      return { isValid: false, helperText: translate('validation:RequiredField') };
    }

    return { isValid: true };
  };

  const columns = [
    {
      editable: 'always',
      title: translate('common:Category'),
      field: 'chat_message_category_id',
      lookup: chatCategoryOptions,
      customType: 'filterLookup',
      validate: validateLookupField('chat_message_category_id'),
      required: true,
    },
    {
      editable: 'always',
      title: translate('common:Message'),
      field: 'name',
      validate: validateMaxLengthField('name'),
      required: true,
    },
    {
      editable: 'always',
      title: translate('common:EquipmentType'),
      field: 'equipment_type_id',
      lookup: equipmentTypeOptions,
      customType: 'filterLookup',
      render: rowData => renderAllOption(rowData.equipment_type_id, equipmentTypeOptions),
      validate: validateLookupField('equipment_type_id'),
      required: true,
    },
    {
      editable: 'always',
      title: translate('common:EquipmentGroup'),
      field: 'equip_group_id',
      customType: 'filterLookup',
      lookup: equipmentGroupOptions,
      lookupFilter: (e, f) => {
        if (e.id === null || e.id === 0) return true;
        return f.equipment_type_id === null
               || String(e.id_equipament) === String(f.equipment_type_id)
               || String(f.equipment_type_id) === '0';
      },
      render: rowData => renderAllOption(rowData.equip_group_id, equipmentGroupOptions),
      validate: validateLookupField('equip_group_id'),
      required: true,
    },
    {
      editable: 'always',
      title: translate('common:Status'),
      field: 'active',
      customType: 'boolean',
    },
  ];

  const columnsMessages = [
    {
      editable: 'always',
      title: translate('common:NameMessageCategory'),
      field: 'name',
      validate: validateLookupField('name'),
      required: true,
    },
    {
      editable: 'always',
      title: translate('common:EquipmentType'),
      field: 'equipment_type_id',
      lookup: equipmentTypeOptions,
      customType: 'filterLookup',
      render: rowData => renderAllOption(rowData.equipment_type_id, equipmentTypeOptions),
      required: true,
    },
    {
      editable: 'always',
      title: translate('common:EquipmentGroup'),
      field: 'equip_group_id',
      customType: 'filterLookup',
      lookup: equipmentGroupOptions,
      lookupFilter: (e, f) => {
        if (e.id === null || e.id === 0) return true;
        return f.equipment_type_id === null
               || String(e.id_equipament) === String(f.equipment_type_id)
               || String(f.equipment_type_id) === '0';
      },
      render: rowData => renderAllOption(rowData.equip_group_id, equipmentGroupOptions),
      validate: validateLookupField('equip_group_id'),
      required: true,
    },
    {
      editable: 'always',
      title: translate('common:Status'),
      field: 'active',
      customType: 'boolean',
    },
  ];

  const countMessagesForCategory = categoryId => (
    chatResponse.filter(response => response.chat_message_category_id === categoryId).length
  );

  const checkMaxMessagesPerCategory = categoryId => countMessagesForCategory(categoryId) >= 6;

  return (
    <div className={classes.tabContainer}>
      <hr style={{ margin: '0px' }} />
      {!expanded && (
        <PF2MDataTable
          title={translate('common:MessageRegistration')}
          options={{
            showTitle: true,
            selection: true,
            filtering: true,
            cacheKey: 'chatResponse',
            pageSize: 10,
            pageSizeOptions: [10, 50, 100],
            maxBodyHeight: 'calc(100vh - 350px)',
            minBodyHeight: 'calc(100vh - 350px)',
            hideDelete: false,
          }}
          onAdd={newData => dispatch(createChatResponseItem({ ...newData, isNew: true }))}
          onDiscard={() => dispatch(resetChatResponse())}
          onApply={() => {
            const categoryCounts = {};
            const isEditing = chatResponse.some(item => item.isNew === false);
            chatResponse.forEach((item) => {
              const categoryId = item.chat_message_category_id;
              categoryCounts[categoryId] = (categoryCounts[categoryId] || 0) + 1;
            });

            for (const categoryId in categoryCounts) {
              if (!isEditing && categoryCounts[categoryId] > 6) {
                setAlertOpenResponse(true);
                return;
              }
            }
            // remove isNew antes de enviar a requisição para o backend
            const payload = chatResponse.map(({ isNew, ...rest }) => rest);

            dispatch(updateChatResponse(payload));
          }}
          onChange={(newData) => {
            const isEditing = newData.some(item => item.isNew === false);
            newData.forEach(({ ...row }) => {
              const categoryId = row?.chat_message_category_id;
              if (!isEditing && checkMaxMessagesPerCategory(categoryId)) {
                setAlertOpenResponse(true);
                return;
              }
              dispatch(
                setChatResponseValues({
                  ...row,
                  equipment_type_id: row.equipment_type_id === 0 ? null : row.equipment_type_id,
                  equip_group_id: row.equip_group_id === 0 ? null : row.equip_group_id,
                  active: row.active ? 1 : 0,
                }),
              );
            });
          }}
          onDelete={(newData) => {
            newData.forEach((e) => {
              dispatch(deleteChatResponseItem(e));
            });
          }}
          initialFormData={chatResponse}
          columns={columns}
          data={formattedChatResponses.length > 0 ? formattedChatResponses : []}
        />
      )}

      <PF2MAlertDialog
        cancelText={translate('common:Cancel')}
        confirmText={translate('common:Ok')}
        description={translate('validation:NotificationMaximumMessage')}
        open={alertOpenResponse}
        onConfirm={() => {
          dispatch(resetChatResponse());
          setAlertOpenResponse(false);
        }}
        onClose={() => {
          setAlertOpenResponse(false);
        }}
      />

      <Accordion
        defaultExpanded
        onChange={() => {
          setExpanded(!expanded);
        }}
        expanded={expanded}
      >
        <AccordionSummary
          expandIcon={expanded ? <ExpandLess /> : <ExpandMore />}
          id="1"
        >
          <Typography>{translate('common:MessageCategory')}</Typography>
        </AccordionSummary>
        <AccordionDetails style={{ overflow: 'auto', display: 'block' }}>
          <PF2MDataTable
            title={translate('common:MessageCategory')}
            options={{
              showTitle: false,
              search: false,
              selection: true,
              hideDelete: false,
              cacheKey: 'chatCategory',
              maxBodyHeight: 'calc(100vh - 350px)',
              minBodyHeight: 'calc(100vh - 350px)',
            }}
            onAdd={newData => dispatch(createChatCategoryItem(newData))}
            onDiscard={() => dispatch(resetChatCategory())}
            onApply={() => dispatch(updateChatCategory(chatCategory))}
            onChange={(newData) => {
              newData.forEach(({ ...row }) => {
                dispatch(
                  setChatCategoryValues({
                    ...row,
                    equipment_type_id: row.equipment_type_id === 0 ? null : row.equipment_type_id,
                    equip_group_id: row.equip_group_id === 0 ? null : row.equip_group_id,
                    active: row.active ? 1 : 0,
                  }),
                );
              });
            }}
            onDelete={(newData) => {
              newData.forEach((e) => {
                dispatch(deleteChatCategoryItem(e));
              });
            }}
            initialFormData={chatCategory}
            columns={columnsMessages}
            data={formattedCategories.length > 0 ? formattedCategories : []}
          />
        </AccordionDetails>
      </Accordion>

      <PF2MAlertDialog
        cancelText={translate('common:Cancel')}
        confirmText={translate('common:Ok')}
        description={translate('validation:NotificationMaximumCategory')}
        open={alertOpenCategory}
        onConfirm={() => {
          if (chatCategory.length >= 10) {
            dispatch(resetChatCategory());
            setAlertOpenCategory(false);
          }
        }}
        onClose={() => {
          setAlertOpenCategory(false);
        }}
      />
    </div>
  );
};

MessagesRegistationTab.propTypes = {
  classes: PropTypes.object.isRequired,
};

MessagesRegistationTab.defaultProps = {};

export default withStyles(styles)(MessagesRegistationTab);
